import type { DiscoveryFacts, Evidence, EvidenceSource, IndexDef, PlaybookFacts } from '@/api/types/readiness'
import { allObjectives } from '@/lib/indexes/registry'
import { uid } from '@/lib/readiness/util'

// ── Evidence from facts (§7, Note 4) ────────────────────────────────────────
// What a sync records from the Discovery and Playbook facts. Idempotent on
// (source, external_id): the store only keeps items whose key is new.

export function evidenceFromFacts(
  orgId: string,
  indexId: string,
  facts: { discovery: DiscoveryFacts; playbook: PlaybookFacts },
  index: IndexDef,
  at: string = new Date().toISOString()
): Evidence[] {
  const out: Evidence[] = []
  const has = (code: string) => allObjectives(index).some((o) => o.objective.code === code)
  const base = (code: string, source: EvidenceSource, external_id: string, title: string, summary: string, link: string | null, occurred_at = at): Evidence => ({
    id: uid('ev'),
    org_id: orgId,
    index_id: indexId,
    objective_code: code,
    source,
    external_id,
    title,
    summary,
    link,
    occurred_at,
    added_by: 'system',
    archived_at: null,
  })
  const d = facts.discovery
  if (d.connected) {
    d.inventories.forEach((inv) => {
      if (has('RA-1'))
        out.push(
          base(
            'RA-1',
            'discovery',
            `inventory:${inv.id}`,
            `Inventory · ${inv.name}`,
            `${inv.prioritised ? 'Prioritised / CII inventory' : 'Inventory'}${inv.business_unit ? ` · ${inv.business_unit}` : ''}${
              inv.asset_count != null ? ` · ${inv.asset_count.toLocaleString()} assets` : ''
            }${d.cbom_inventory_ids.includes(inv.id) ? ' · CBOM present' : ' · no CBOM yet'}`,
            `/inventories/${inv.id}`
          )
        )
    })
    if (d.cert_key_inventory && has('TE-2'))
      out.push(base('TE-2', 'discovery', 'cert-key-inventory', 'Certificate / key inventory present', `Discovery holds a certificate or key inventory across ${d.source_count} sources.`, '/sources'))
    d.vendor_components.forEach((v) => {
      if (has('EE-1'))
        out.push(base('EE-1', 'discovery', `vendor:${v.id}`, `Vendor-supplied component · ${v.name}`, `${v.type} identified by Discovery as vendor-supplied cryptography.`, v.inventory_id ? `/inventories/${v.inventory_id}` : '/sources'))
    })
  }
  const p = facts.playbook
  p.plans.forEach((plan) => {
    if (has('TE-1'))
      out.push(
        base('TE-1', 'playbook', `plan:${plan.id}:${plan.status}`, `Playbook plan · ${plan.title}`, `Status ${plan.status.replace('_', ' ')}${plan.owner ? ` · owner ${plan.owner}` : ''}`, `/readiness/TE-1`, plan.updated_at)
      )
  })
  // Module 3 (I7): the Issuing CAs are EE-1's third-party certificate
  // dependencies; a remediation row with vulnerable assets is a quantum-safe
  // replacement option identified for TE-1.
  if (p.service_connected) {
    p.issuing_cas.forEach((ca) => {
      if (has('EE-1'))
        out.push(
          base('EE-1', 'playbook', `issuing-ca:${ca.id}`, `Issuing CA · ${ca.issuing_ca}`, `${ca.ca_owner} · identified by ${ca.matched_by.replace('_', ' ')}${ca.inventory_names.length ? ` · ${ca.inventory_names.join(', ')}` : ''}`, ca.system_ids[0] ? `/inventories/${ca.system_ids[0]}` : null)
        )
    })
    p.remediations
      .filter((r) => r.vulnerable_count > 0)
      .forEach((r) => {
        if (has('TE-1'))
          out.push(
            base('TE-1', 'playbook', `remediation:${r.key}`, `Replacement option · ${r.asset}`, `${r.nist_rule} → ${r.nist_remediation} (${r.vulnerable_count.toLocaleString()} vulnerable ${r.vulnerable_count === 1 ? 'asset' : 'assets'})`, r.system_ids[0] ? `/inventories/${r.system_ids[0]}` : `/readiness/TE-1`)
          )
      })
  }
  return out
}
